import { useState } from "react";
import "./contact-form.css";
import { useTranslation } from "react-i18next";

export default function ContactForm() {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    requirement: "",
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");

  // Single-field validator (used for real-time + blur validation)
  const validateField = (name, value) => {
    let error = "";

    if (name === "firstName" && !value.trim()) error = "First name is required";
    if (name === "lastName" && !value.trim()) error = "Last name is required";

    if (name === "email") {
      if (!value.trim()) error = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(value)) error = "Invalid email";
    }

    if (name === "phone") {
      const phoneRegex = /^[0-9+\-()\s]*$/;
      if (!value.trim()) error = "Phone number is required";
      else if (!phoneRegex.test(value)) error = "Invalid phone number";
    }

    return error;
  };

  // Full form validator (for submit)
  const validate = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    return newErrors;
  };

  // Live validation as user types
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Validate on blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  //validate on keydown for phone number field
  const handlePhoneKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];

    const allowedChars = /[0-9+\-()\s]/;

    if (
      !allowedKeys.includes(e.key) && // allow control keys
      !allowedChars.test(e.key) // block anything not allowed
    ) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus("Sending...");

    const form = new FormData();
    form.append("access_key", process.env.REACT_APP_WEB3FORMS_KEY); //Web3Forms access key from .env.local
    form.append("first_Name", formData.firstName);
    form.append("last_Name", formData.lastName);
    form.append("email", formData.email);
    form.append("phone", formData.phone);
    form.append("requirement", formData.requirement);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: form,
      });

      const result = await response.json();
      if (result.success) {
        setStatus("Message sent successfully!");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          requirement: "",
        });
      } else {
        setStatus("Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus("Failed to send. Please check your connection.");
    }
  };

  return (
    <section className="contact-container" id="contactForm">
      <div className="contact-form-card">
        <h2>{t("Get in Touch With Us")}</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t("First Name")}<span className="mandatory">*</span></label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Enter your first name")}
              />
              {errors.firstName && (
                <span className="error">{t(errors.firstName)}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t("Last Name")}<span className="mandatory">*</span></label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Enter your last name")}
              />
              {errors.lastName && (
                <span className="error">{t(errors.lastName)}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">{t("Email Address")}<span className="mandatory">*</span></label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Enter your email")}
              />
              {errors.email && <span className="error">{t(errors.email)}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t("Phone Number")}<span className="mandatory">*</span></label>
              <input
                id="phone"
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onKeyDown={handlePhoneKeyDown}
                onBlur={handleBlur}
                placeholder={t("Enter your phone number")}
              />
              {errors.phone && <span className="error">{t(errors.phone)}</span>}
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="requirement">{t("How can we help you?")}</label>
            <textarea
              id="requirement"
              name="requirement"
              value={formData.requirement}
              onChange={handleChange}
              placeholder={t("Tell us about your requirement")}
            ></textarea>
          </div>

            <button className="submit-contact-btn" type="submit">
                {t("Submit")}
            </button>
          {status && <p className="status">{t(status)}</p>}
        </form>
      </div>
    </section>
  );
}
